import { SLEEP_STAGE_COLORS } from "@rekonas/signal-viewer";
import { useId, useState, type MouseEvent } from "react";
import type { HypnogramResult } from "@rekonas/data-access";

export interface StageInterval {
  start: number;
  end: number;
  stage: string;
  coverage: "scored" | "unknown" | "unscored" | "missing";
}

export const STANDARD_STAGE_ORDER = ["Wake", "REM", "N1", "N2", "N3"] as const;
export const STAGE_COLORS = SLEEP_STAGE_COLORS;

export function stageOrder(...results: Array<HypnogramResult | null | undefined>): string[] {
  const extras = new Set<string>();
  for (const result of results) for (const label of result?.labels || []) {
    if (label && label !== "Unknown" && label !== "UnscoredTail" &&
        !STANDARD_STAGE_ORDER.includes(label as typeof STANDARD_STAGE_ORDER[number])) extras.add(label);
  }
  return [...STANDARD_STAGE_ORDER, ...[...extras].sort()];
}

export function hypnogramIntervals(result: HypnogramResult, duration: number): StageInterval[] {
  if (result.state !== "available" || !Array.isArray(result.stages) || !result.stages.length || !Array.isArray(result.labels) ||
      !Number.isFinite(result.start_seconds) || !Number.isFinite(result.epoch_seconds) ||
      !Number.isFinite(duration) || duration <= 0 || (result.epoch_seconds || 0) <= 0 ||
      (result.start_seconds || 0) < 0) return [];
  const origin = result.start_seconds || 0;
  const epoch = result.epoch_seconds || 0;
  const intervals: StageInterval[] = [];
  function add(start: number, end: number, stage: string, coverage: StageInterval["coverage"]) {
    const left = Math.max(0, Math.min(duration, start));
    const right = Math.max(0, Math.min(duration, end));
    if (right <= left) return;
    const prior = intervals.at(-1);
    if (prior && prior.end === left && prior.stage === stage && prior.coverage === coverage) prior.end = right;
    else intervals.push({ start: left, end: right, stage, coverage });
  }
  add(0, origin, "Missing coverage", "missing");
  result.stages.forEach((index, position) => {
    const stage = index === null ? "Unknown" : result.labels?.[index];
    add(origin + position * epoch, origin + (position + 1) * epoch,
      stage || "Unknown", index !== null && stage && stage !== "Unknown" ? "scored" : "unknown");
  });
  const scoredEnd = origin + result.stages.length * epoch;
  const tail = Math.max(0, result.unscored_tail_seconds || 0);
  add(scoredEnd, scoredEnd + tail, "Unscored tail", "unscored");
  add(scoredEnd + tail, duration, "Missing coverage", "missing");
  return intervals;
}

export function hypnogramTime(seconds: number): string {
  const rounded = Math.max(0, Math.round(seconds));
  return `${String(Math.floor(rounded / 3600)).padStart(2, "0")}:${String(Math.floor((rounded % 3600) / 60)).padStart(2, "0")}:${String(rounded % 60).padStart(2, "0")}`;
}

function intervalAt(intervals: StageInterval[], seconds: number): StageInterval | undefined {
  return intervals.find((item) => item.start <= seconds && seconds < item.end);
}

export function hypnogramSelection(result: HypnogramResult, duration: number, seconds: number) {
  if (!Number.isFinite(seconds) || seconds < 0 || seconds > duration) return null;
  const intervals = hypnogramIntervals(result, duration);
  const interval = seconds === duration ? intervals.at(-1) : intervalAt(intervals, seconds);
  if (!interval) return null;
  let start = interval.start, end = interval.end;
  if (interval.coverage === "scored" || interval.coverage === "unknown") {
    const origin = result.start_seconds || 0, epoch = result.epoch_seconds || 0;
    const index = Math.min((result.stages?.length || 1) - 1, Math.floor((seconds - origin) / epoch));
    start = origin + index * epoch; end = Math.min(duration, start + epoch);
  }
  return { time_seconds: seconds, start_seconds: start, end_seconds: end,
    stage: interval.coverage === "scored" ? interval.stage : null, coverage: interval.coverage };
}

export function ResultHypnogramPlot({ result, durationSeconds, order, highlight, cursorSeconds,
  onCursorChange, onSeek, heading, caption }: {
  result: HypnogramResult;
  heading?: string;
  caption?: string;
  durationSeconds: number;
  order?: string[];
  highlight?: { start: number; end: number; full?: boolean };
  cursorSeconds?: number | null;
  onCursorChange?: (seconds: number | null) => void;
  onSeek?: (seconds: number) => void;
}) {
  const gapPattern = `sleep-gap-${useId().replaceAll(":", "")}`;
  const intervals = hypnogramIntervals(result, durationSeconds);
  const lanes = order || stageOrder(result);
  const [localCursor, setLocalCursor] = useState<number | null>(null);
  const cursor = cursorSeconds === undefined ? localCursor : cursorSeconds;
  const active = cursor == null ? undefined : intervalAt(intervals, cursor);
  const axis = [0, 0.25, 0.5, 0.75, 1];
  const height = lanes.length * 25;
  const width = 1000;
  const x = (seconds: number) => Math.max(0, Math.min(width, seconds / durationSeconds * width));
  function pointerSeconds(event: MouseEvent<SVGSVGElement>) {
    const bounds = event.currentTarget.getBoundingClientRect();
    return Math.max(0, Math.min(durationSeconds, (event.clientX - bounds.left) / Math.max(1, bounds.width) * durationSeconds));
  }
  function move(event: MouseEvent<SVGSVGElement>) {
    const seconds = pointerSeconds(event);
    setLocalCursor(seconds); onCursorChange?.(seconds);
  }
  if (!intervals.length) return <>{heading && <h2>{heading}</h2>}{caption && <p className="operator-muted">{caption}</p>}<p className="result-hypnogram-empty">No saved sleep stages are available for this result.</p></>;
  const bandStart = Math.max(0, Math.min(durationSeconds, highlight?.start ?? 0));
  const bandEnd = Math.max(bandStart, Math.min(durationSeconds, highlight?.end ?? 0));
  const readout = (<div className="result-hypnogram-info" role="status">{active
      ? `${active.stage} · ${hypnogramTime(active.start)}–${hypnogramTime(active.end)} elapsed`
      : highlight ? `${highlight.full ? "Full night" : "Visible window"} · ${hypnogramTime(bandStart)}–${hypnogramTime(bandEnd)} elapsed`
      : "Elapsed time from recording start · hover or focus a stage interval"}</div>);
  const legend = (<div className="result-hypnogram-legend">{lanes.map((label) => <span key={label}><i style={{ backgroundColor: STAGE_COLORS[label] || "#7b8290" }} />{label}</span>)}
      <span><i className="result-hypnogram-unknown" style={{ background: `repeating-linear-gradient(135deg,${STAGE_COLORS.Unscored},${STAGE_COLORS.Unscored} 3px,${STAGE_COLORS.Unknown} 3px,${STAGE_COLORS.Unknown} 6px)` }} />Unknown / unscored / missing</span></div>);
  return <div className="result-hypnogram">
    {heading && <div className="result-hypnogram-heading"><h2>{heading}</h2>{legend}</div>}
    {caption && <div className="result-hypnogram-caption"><p className="operator-muted">{caption}</p>{readout}</div>}
    <div className="result-hypnogram-main">
      <div className="result-hypnogram-labels" aria-hidden="true">{lanes.map((label) => <span key={label}>{label}</span>)}</div>
      <svg aria-label="Sleep stages by elapsed recording time" className="result-hypnogram-svg"
        onClick={onSeek ? (event) => onSeek(pointerSeconds(event)) : undefined}
        onKeyDown={onSeek ? (event) => { if (event.key === "Enter" && cursor != null) onSeek(cursor); } : undefined}
        onBlur={() => { setLocalCursor(null); onCursorChange?.(null); }}
        onMouseLeave={() => { setLocalCursor(null); onCursorChange?.(null); }} onMouseMove={move}
        role="img" tabIndex={onSeek ? 0 : undefined} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
        <defs><pattern id={gapPattern} width="12" height="12" patternUnits="userSpaceOnUse">
          <rect width="12" height="12" fill={STAGE_COLORS.Unscored} />
          <path d="M-3 3L3 -3M0 12L12 0M9 15L15 9" stroke={STAGE_COLORS.Unknown} strokeWidth="6" />
        </pattern></defs>
        {lanes.map((label, index) => <rect fill={STAGE_COLORS[label] || "#7b8290"} fillOpacity="0.1"
          height="21" key={label} width={width} x="0" y={index * 25 + 2} />)}
        {axis.map((ratio) => <line className="result-hypnogram-grid" key={ratio} x1={ratio * width} x2={ratio * width} y1="0" y2={height} />)}
        {intervals.filter((item) => item.coverage !== "scored").map((item) => <rect
          style={{ fill: `url(#${gapPattern})` }} className={`result-hypnogram-gap gap-${item.coverage}`} key={`${item.start}-${item.coverage}`}
          onFocus={() => { setLocalCursor(item.start); onCursorChange?.(item.start); }}
          tabIndex={0} x={x(item.start)} width={x(item.end) - x(item.start)} y="0" height={height}>
          <title>{item.stage}: {hypnogramTime(item.start)}–{hypnogramTime(item.end)}</title>
        </rect>)}
        {intervals.filter((item) => item.coverage === "scored").map((item) => {
          const lane = lanes.indexOf(item.stage);
          return lane < 0 ? null : <rect fill={STAGE_COLORS[item.stage] || "#7b8290"}
            onFocus={() => { setLocalCursor(item.start); onCursorChange?.(item.start); }} tabIndex={0}
            key={`${item.start}-${item.stage}`} x={x(item.start)} width={Math.max(0.6, x(item.end) - x(item.start))}
            y={lane * 25 + 9} height="7" rx="3"><title>{item.stage}: {hypnogramTime(item.start)}–{hypnogramTime(item.end)}</title></rect>;
        })}
        {intervals.slice(1).map((item, index) => {
          const previous = intervals[index];
          if (previous.coverage !== "scored" || item.coverage !== "scored" || previous.end !== item.start || previous.stage === item.stage) return null;
          return <line className="result-hypnogram-transition" key={`transition-${index}`}
            x1={x(item.start)} x2={x(item.start)} y1={lanes.indexOf(previous.stage) * 25 + 12.5}
            y2={lanes.indexOf(item.stage) * 25 + 12.5} />;
        })}
        {highlight && <rect className="result-hypnogram-window" x={x(bandStart)}
          width={Math.max(1.5, x(bandEnd) - x(bandStart))} y="0" height={height}>
          <title>{highlight.full ? "Full night" : "Visible window"}: {hypnogramTime(bandStart)}–{hypnogramTime(bandEnd)}</title>
        </rect>}
        {cursor != null && <line className="result-hypnogram-cursor" x1={x(cursor)} x2={x(cursor)} y1="0" y2={height} />}
      </svg>
    </div>
    <div className="result-hypnogram-axis"><span aria-hidden="true" />{axis.map((ratio) => <span key={ratio}>{hypnogramTime(durationSeconds * ratio).slice(0, 5)}</span>)}</div>
    {!caption && readout}
    {!heading && legend}
  </div>;
}
